import { useCallback, useEffect, useState } from "react";
import { Heart, Link2, Loader2, Search, Send, Trophy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCountdown, pad } from "@/lib/contest";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { usePageMeta } from "@/lib/seo";
import { HeartMap, CityPrompt } from "@/components/HeartMap";

export const useHeart = () => {
  const [c, setC] = useState(undefined);
  const load = useCallback(() => api.get("/heart").then(({ data }) => setC(data.chain)).catch(() => setC(null)), []);
  useEffect(() => { load(); }, [load]);
  return [c, load];
};

export const GlowHeart = ({ size = "w-40 h-40", beating = true }) => (
  <div className={`relative ${size} mx-auto`} aria-hidden>
    {beating && <span className="absolute inset-0 rounded-full bg-rose-400/40 blur-3xl animate-pulse" />}
    <Heart className={`relative w-full h-full fill-rose-500 text-rose-500 drop-shadow-[0_0_30px_rgba(244,63,94,0.8)] ${beating ? "heart-beat" : "opacity-40 grayscale"}`} />
  </div>
);

const Timer = ({ end }) => {
  const { h, m, s, left } = useCountdown(end);
  return <span className={`font-mono font-extrabold tabular-nums ${left < 3 * 3600000 ? "text-red-600" : "text-slate-900"}`} data-testid="heart-countdown">{pad(h)}:{pad(m)}:{pad(s)}</span>;
};

function PassPanel({ c, onDone }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [token, setToken] = useState(c.invite_token);
  useEffect(() => { const t = setTimeout(() => api.get("/community/members", { params: { q } }).then(({ data }) => setList(data.filter((u) => u.user_id !== user?.user_id).slice(0, 8))).catch(() => {}), 250); return () => clearTimeout(t); }, [q, user?.user_id]);
  const give = async (u) => {
    if (!window.confirm(`Herz an ${u.name} weitergeben?`)) return;
    setBusy(u.user_id);
    try { const { data } = await api.post("/heart/pass", { to_id: u.user_id }); toast.success(`💖 Weitergegeben! ${u.name} ist Station ${data.station} – +${c.pass_points} Punkte für dich`); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(null); }
  };
  const share = async () => {
    try {
      const t = token || (await api.post("/heart/invite")).data.token;
      setToken(t);
      const url = `${window.location.origin}/herz/${t}`;
      const text = `💖 Ich schenke dir das Herz der itzPlay Herz-Kette! Du bist Station ${c.length + 1} – nimm es an und gib es weiter, bevor die Kette reißt:`;
      if (navigator.share) await navigator.share({ title: "Die Herz-Kette", text, url }).catch(() => {});
      else {
        try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Link kopiert – schick ihn per WhatsApp oder Insta!"); }
        catch { window.prompt("Kopiere deinen Herz-Link:", url); }
      }
    } catch (e) { toast.error(formatErr(e)); }
  };
  const wa = token ? `https://api.whatsapp.com/send?text=${encodeURIComponent(`💖 Ich schenke dir das Herz der itzPlay Herz-Kette! Nimm es an, bevor die Kette reißt: ${window.location.origin}/herz/${token}`)}` : null;
  return (
    <div className="rounded-3xl bg-white border-2 border-rose-200 p-5 space-y-4 shadow-xl shadow-rose-200/60" data-testid="heart-pass-panel">
      <div>
        <p className="font-display text-xl font-extrabold">Du hast das Herz! 💖</p>
        <p className="text-sm text-slate-600">Gib es in <Timer end={c.deadline} /> weiter – sonst reißt die Kette. Jede Person kann das Herz nur einmal pro Kette bekommen.</p>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-bold flex items-center gap-1.5"><Share2 className="w-4 h-4 text-rose-500" /> An jemanden außerhalb der App</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={share} data-testid="heart-share-link" className="h-11 px-5 rounded-full pink-gradient text-white font-bold text-sm inline-flex items-center gap-2 shadow-lg shadow-rose-200 active:scale-95 transition-transform"><Link2 className="w-4 h-4" /> Herz-Link teilen</button>
          {wa && <a href={wa} target="_blank" rel="noreferrer" data-testid="heart-share-whatsapp" className="h-11 px-5 rounded-full bg-emerald-500 text-white font-bold text-sm inline-flex items-center">WhatsApp</a>}
        </div>
        <p className="text-xs text-slate-400">Wer den Link zuerst öffnet und annimmt (kostenlos registrieren), bekommt das Herz.</p>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-bold flex items-center gap-1.5"><Send className="w-4 h-4 text-rose-500" /> Oder direkt an ein Mitglied</p>
        <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name suchen…" data-testid="heart-search" className="w-full h-11 pl-9 pr-3 rounded-2xl border border-pink-100 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" /></div>
        <ul className="divide-y divide-pink-50">
          {list.map((u) => (
            <li key={u.user_id} className="flex items-center gap-3 py-2">
              <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-9 h-9" noStory />
              <span className="flex-1 min-w-0 truncate text-sm font-semibold">{u.name}</span>
              <button onClick={() => give(u)} disabled={!!busy} data-testid={`heart-give-${u.user_id}`} className="h-8 px-3 rounded-full bg-rose-50 text-rose-600 text-xs font-bold inline-flex items-center gap-1 hover:bg-rose-100">{busy === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5" />} Geben</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const Path = ({ c }) => (
  <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-3" data-testid="heart-path">
    <p className="font-bold">Die Reise des Herzens</p>
    {c.path.length === 0 ? <p className="text-sm text-slate-400">Das Herz wartet noch auf seine erste Station …</p> : (
      <ol className="relative border-l-2 border-dashed border-rose-200 ml-3 space-y-3">
        {c.path.map((p) => (
          <li key={p.station} className="pl-5 relative" data-testid={`heart-station-${p.station}`}>
            <Heart className="absolute -left-[9px] top-2 w-4 h-4 fill-rose-500 text-rose-500" />
            <div className="flex items-center gap-2">
              <UserAvatar userId={p.user?.user_id} name={p.user?.name} picture={p.user?.picture} size="w-8 h-8" noStory />
              <span className="text-sm"><b>#{p.station}</b> <UserLink id={p.user?.user_id}>{p.user?.name || "Mitglied"}</UserLink>{p.city && <span className="ml-1 text-[11px] text-slate-400">· {p.city}</span>}{p.via === "link" && <span className="ml-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded-full px-1.5 py-0.5">per Link</span>}</span>
              <span className="ml-auto text-[11px] text-slate-400">{new Date(p.at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          </li>
        ))}
      </ol>
    )}
  </div>
);

export default function HeartChain() {
  const { user } = useAuth();
  const [c, load] = useHeart();
  usePageMeta({ title: "Die Herz-Kette – itzPlay" });
  const start = () => api.post("/heart/admin/start").then(() => { toast.success("Die Herz-Kette ist gestartet – gib das Herz jetzt weiter!"); load(); }).catch((e) => toast.error(formatErr(e)));
  const reset = () => window.confirm("Wirklich komplett zurücksetzen? Alle bisherigen Ketten, der Rekord und die Karte werden gelöscht. Danach startet Herz-Kette #1 bei dir und alle bekommen eine Push-Nachricht.")
    && api.post("/heart/admin/reset").then(() => { toast.success("Alles zurückgesetzt – Herz-Kette #1 ist gestartet!"); load(); }).catch((e) => toast.error(formatErr(e)));
  if (c === undefined) return <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-rose-400" /></div>;
  const active = c?.status === "active";
  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-10" data-testid="heart-chain-page">
      <section className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-b from-rose-50 to-white border border-pink-100 p-6 sm:p-10 text-center space-y-4">
        <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-rose-500">Nur bei itzPlay</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Die Herz-Kette{c ? ` #${c.number}` : ""}</h1>
        <GlowHeart beating={active} />
        {!c ? <p className="text-slate-600">Bald schickt Itzcouple das erste Herz auf die Reise. Vielleicht landet es bei dir!</p> : active ? (
          <div className="space-y-2">
            <p className="font-display text-5xl font-extrabold text-rose-600" data-testid="heart-length">{c.length}</p>
            <p className="text-sm font-bold text-slate-500 -mt-1">Stationen</p>
            {c.holder && <p className="text-slate-700 flex items-center justify-center gap-2" data-testid="heart-holder">Gerade bei <UserAvatar userId={c.holder.user_id} name={c.holder.name} picture={c.holder.picture} size="w-7 h-7" noStory /> <b>{c.is_holder ? "dir!" : c.holder.name}</b> · noch <Timer end={c.deadline} /></p>}
          </div>
        ) : (
          <div className="space-y-1" data-testid="heart-broken"><p className="font-display text-2xl font-extrabold text-slate-700">💔 Gerissen nach {c.length} Stationen</p><p className="text-sm text-slate-500">Die nächste Kette startet bald – schafft ihr einen neuen Rekord?</p></div>
        )}
        <div className="flex flex-wrap justify-center gap-2 text-xs font-bold">
          <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 inline-flex items-center gap-1" data-testid="heart-record"><Trophy className="w-3.5 h-3.5" /> Rekord: {c?.record || 0} {c?.record === 1 ? "Station" : "Stationen"}</span>
          {active && c.next_milestone && <span className="px-3 py-1.5 rounded-full bg-rose-50 text-rose-600">Bei {c.next_milestone} Stationen: +{c.next_bonus} Punkte für alle in der Kette</span>}
          {c?.my_station && <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700">Du warst Station {c.my_station} 💖</span>}
        </div>
      </section>
      {c?.need_city && <CityPrompt c={c} onDone={load} />}
      {active && c.is_holder && <PassPanel c={c} onDone={load} />}
      {c && <HeartMap c={c} />}
      <div className="rounded-3xl bg-white border border-pink-100 p-5 text-sm text-slate-600 space-y-1.5" data-testid="heart-rules">
        <p className="font-bold text-slate-900">So funktioniert's</p>
        <p>💖 Wer das Herz hat, gibt es innerhalb von 24 Stunden weiter – an ein Mitglied oder per Link an jemand Neues.</p>
        <p>✨ Fürs Weitergeben gibt's +{c?.pass_points || 10} Punkte, bei Meilensteinen bekommen alle in der Kette einen Bonus.</p>
        <p>💔 Wird das Herz nicht rechtzeitig weitergegeben, reißt die Kette. Jede Person kann das Herz pro Kette nur einmal haben.</p>
      </div>
      {c && <Path c={c} />}
      {user?.role === "admin" && !active && <button onClick={start} data-testid="heart-admin-start" className="w-full h-12 rounded-full pink-gradient text-white font-bold shadow-lg shadow-rose-200">💖 Neue Herz-Kette starten (Admin)</button>}
      {user?.role === "admin" && c && <button onClick={reset} data-testid="heart-admin-reset" className="w-full h-11 rounded-full border-2 border-rose-200 text-rose-600 font-bold hover:bg-rose-50 transition-colors">Alles zurücksetzen & neu starten</button>}
    </div>
  );
}
